import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import { ArrowDown, Loader2, User } from 'lucide-react'

export type AgentSummary = {
  id: number
  name: string
  slug: string
  subtitle?: string | null
  instructions?: string | null
  message_limit?: number | null
  model_id?: string | null
  top_k?: number | null
  disable_own_knowledge: boolean
  icon_preview_url?: string | null
}

export type AgentSandboxChatProps = {
  agent: AgentSummary
  conversationId: number | null
  onConversationId: (id: number) => void
  onQuotaBypassNotice?: () => void
}

export type SandboxMessage = {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  pending?: boolean
}

export default function AgentSandboxChat({ agent, conversationId, onConversationId, onQuotaBypassNotice }: AgentSandboxChatProps) {
  const [input, setInput] = useState('')
  const [history, setHistory] = useState<SandboxMessage[]>([])
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setHistory([])
    setInput('')
    setError(null)
  }, [agent.id])

  const handleSubmit = useCallback(async () => {
    if (!input.trim()) return
    const question = input.trim()
    const userMsg: SandboxMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: question,
    }
    setHistory(prev => [...prev, userMsg, { id: `pending-${Date.now()}`, role: 'assistant', content: '', pending: true }])
    setInput('')
    setSending(true)
    setError(null)

    try {
      const payload: any = {
        question,
        agent_slug: agent.slug,
        sandbox: true,
      }
      if (conversationId) {
        payload.conversation_id = conversationId
      }

      const { data } = await api.post('/chat/agent', payload)
      if (data?.conversation_id) {
        onConversationId(data.conversation_id)
      }
      if (onQuotaBypassNotice) {
        onQuotaBypassNotice()
      }

      const answer: SandboxMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: data?.answer ?? '[Réponse vide]',
      }

      setHistory(prev => {
        const next = [...prev]
        const pendingIndex = next.findIndex(msg => msg.pending)
        if (pendingIndex !== -1) {
          next.splice(pendingIndex, 1, answer)
        } else {
          next.push(answer)
        }
        return next
      })

      // Emit event for onboarding to detect agent response
      window.dispatchEvent(new CustomEvent('onboarding:agent-response'))
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Erreur sandbox'
      setError(detail)
      setHistory(prev => prev.filter(msg => !msg.pending))
    } finally {
      setSending(false)
    }
  }, [agent.slug, conversationId, input, onConversationId, onQuotaBypassNotice])

  const formattedHistory = useMemo(() => history, [history])

  // Utiliser l'icône de l'agent au lieu du logo de la plateforme
  const agentIcon = agent.icon_preview_url

  return (
    <div className="flex h-full flex-col" data-onboarding="sandbox-chat">
      {/* Chat Messages Container */}
      <div className="flex-1 overflow-y-auto bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 p-6">
        {formattedHistory.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center space-y-8 text-center">
            {/* Animated Agent Icon with Green Glow */}
            <div className="relative">
              <div className="absolute inset-0 animate-pulse rounded-full bg-emerald-500/30 blur-3xl" />
              {agentIcon ? (
                <img 
                  src={agentIcon} 
                  alt={agent.name} 
                  className="relative h-32 w-32 rounded-full border-4 border-emerald-500/50 object-cover shadow-2xl shadow-emerald-500/50" 
                />
              ) : (
                <div className="relative flex h-32 w-32 items-center justify-center rounded-full border-4 border-emerald-500/50 bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-2xl shadow-emerald-500/50">
                  <span className="text-4xl font-bold text-white">{agent.name.charAt(0).toUpperCase()}</span>
                </div>
              )}
            </div>

            {/* Welcome Text */}
            <div className="max-w-md space-y-4">
              <h2 className="text-2xl font-bold text-white">Testez {agent.name} ici</h2>
              <p className="text-base leading-relaxed text-zinc-400">
                Cet espace vous permet de vérifier l'efficacité de votre agent avant de le mettre en ligne. Envoyez un message pour commencer.
              </p>
              <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-4 text-sm text-zinc-500">
                Toutes les conversations ici sont en mode test et n'affecteront pas votre environnement de production.
              </div>
            </div>

            {/* Start Prompt */}
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <ArrowDown className="h-4 w-4" />
              <span>Commencez par envoyer un message</span>
            </div>
          </div>
        ) : (
          <div className="mx-auto max-w-3xl space-y-6">
            {formattedHistory.map((message, index) => (
              <div
                key={message.id}
                className={`flex gap-4 ${message.role === 'user' ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-2 duration-300`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {message.role === 'assistant' && (
                  <div className="relative flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-lg shadow-emerald-500/30">
                    {agentIcon ? (
                      <img src={agentIcon} alt={agent.name} className="h-6 w-6 object-contain" />
                    ) : (
                      <span className="text-sm font-semibold text-white">{agent.name.charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                )}

                <div className={`flex max-w-[70%] flex-col gap-2 ${message.role === 'user' ? 'items-end' : 'items-start'}`}>
                  <div
                    className={`rounded-2xl px-5 py-3 shadow-lg ${
                      message.role === 'user'
                        ? 'bg-emerald-600 text-white'
                        : 'border border-zinc-800 bg-zinc-900/80 text-zinc-100'
                    }`}
                  >
                    {message.pending ? (
                      <div className="flex items-center gap-2 text-sm text-zinc-400">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Réponse en cours…</span>
                      </div>
                    ) : (
                      <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</p>
                    )}
                  </div>
                </div>

                {message.role === 'user' && (
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-zinc-700 text-white shadow-lg">
                    <User className="h-5 w-5" />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="border-t border-zinc-800 bg-red-950/50 px-6 py-3">
          <p className="text-sm text-red-300">{error}</p>
        </div>
      )}

      {/* Input Area */}
      <form
        onSubmit={event => {
          event.preventDefault()
          if (!sending && input.trim()) {
            handleSubmit()
          }
        }}
        className="border-t border-zinc-800 bg-zinc-900/90 p-6"
      >
        <div className="mx-auto flex max-w-3xl items-end gap-3">
          <textarea
            value={input}
            onChange={event => setInput(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                if (!sending && input.trim()) {
                  handleSubmit()
                }
              }
            }}
            placeholder="Message"
            rows={1}
            className="flex-1 resize-none rounded-2xl border border-zinc-700 bg-zinc-800/50 px-4 py-3 text-sm text-white placeholder-zinc-500 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            disabled={sending}
            data-onboarding="sandbox-chat-input"
          />
          <button
            type="submit"
            className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={sending || !input.trim()}
            title="Envoyer (Entrée)"
          >
            {sending ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
